import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react'
import { AlertTriangle, Info, AlertCircle } from 'lucide-react'
import { useTheme, getThemeClass } from './ThemeProvider'

export type ConfirmType = 'warning' | 'danger' | 'info'

interface ConfirmOptions {
  title: string
  message: string | ReactNode
  type?: ConfirmType
  confirmText?: string
  cancelText?: string
  /** When set, user must type this exact string to enable the confirm button */
  requiredInput?: string
  /** Label shown above the input field (default: 'Type "{requiredInput}" to confirm') */
  requiredInputLabel?: string
  /**
   * A single OK button, no Cancel — for an informational refusal with
   * nothing to confirm (#801: the split is refused whole, e.g. the root is
   * an app/project, there's nothing left to act on, or the mover can't
   * manage the root's own access). There is still one action to click
   * through, so this stays a `confirm()` call rather than a bare toast.
   */
  confirmOnly?: boolean
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null)

/** `useConfirm` for a component that may render outside a `ConfirmProvider` (a test, an embed):
 *  null there — the caller decides its fallback. */
export function useConfirmOptional(): ConfirmContextValue['confirm'] | null {
  return useContext(ConfirmContext)?.confirm ?? null
}

export function useConfirm() {
  const context = useContext(ConfirmContext)
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider')
  }
  return context.confirm
}

interface ModalState extends ConfirmOptions {
  resolve: (value: boolean) => void
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [modal, setModal] = useState<ModalState | null>(null)

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setModal({ ...options, resolve })
    })
  }, [])

  const handleConfirm = () => {
    modal?.resolve(true)
    setModal(null)
  }

  const handleCancel = () => {
    modal?.resolve(false)
    setModal(null)
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {modal && (
        <ConfirmModal
          {...modal}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </ConfirmContext.Provider>
  )
}

interface ConfirmModalProps extends ConfirmOptions {
  onConfirm: () => void
  onCancel: () => void
}

function ConfirmModal({
  title,
  message,
  type = 'warning',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  requiredInput,
  requiredInputLabel,
  confirmOnly = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const canConfirm = !requiredInput || inputValue === requiredInput

  useEffect(() => {
    if (requiredInput) inputRef.current?.focus()
  }, [requiredInput])

  const icons = {
    warning: <AlertTriangle size={24} className="text-amber-500" />,
    danger: <AlertCircle size={24} className="text-red-500" />,
    info: <Info size={24} className="text-blue-500" />,
  }

  const confirmButtonStyles = {
    warning: 'bg-amber-600 hover:bg-amber-700',
    danger: 'bg-red-600 hover:bg-red-700',
    info: 'bg-blue-600 hover:bg-blue-700',
  }

  return (
    // z-[400]: a blocking confirm can be spawned from ANY layer, including the
    // top of the modal band — share/access dialogs (z-[100]/[110]), their nested
    // popovers (z-[200]) and dropdown menus (z-[300]/[320]). It must sit above
    // all of them or its buttons render behind the very dialog that opened it
    // (the "Cancel request / Remove access confirm is unclickable" regression).
    // role + aria-modal: the command registry's shortcuts stand aside while it is open (#900) — it takes no focus.
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 animate-fade-in"
        onClick={onCancel}
      />

      {/* Modal */}
      {/* Glass, like every Papan dialog (ws-glass-modal; owner, 2026-10-02). */}
      <div className={`relative z-10 max-w-md w-full ws-glass-modal ws-anim-modal-in ${themeClass}`}>
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-start gap-4">
            <div className="shrink-0 mt-0.5">
              {icons[type]}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold themed-text mb-1.5">
                {title}
              </h3>
              <div className="themed-text-secondary text-sm whitespace-pre-line">
                {message}
              </div>
            </div>
          </div>
          {requiredInput && (
            <div className="mt-4">
              <label className="block text-xs themed-text-secondary mb-1.5">
                {requiredInputLabel || <>Type <span className="font-semibold themed-text">{requiredInput}</span> to confirm</>}
              </label>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && canConfirm) onConfirm() }}
                className="w-full px-3 py-2 text-sm rounded-lg themed-input themed-border border"
                placeholder={requiredInput}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
        </div>

        <div className="ws-glass-footer flex justify-end gap-2 px-5 py-3">
          {!confirmOnly && (
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm themed-text-muted hover:themed-text rounded-lg ws-glass-well transition-colors"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            className={`px-4 py-1.5 text-white rounded-lg text-sm font-medium transition-colors ${confirmButtonStyles[type]} ${!canConfirm ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}
